import React, { useState } from 'react';
import {
  Code,
  Terminal,
  Key,
  Shield,
  Layers,
  Activity,
  Globe,
  Database,
  CheckCircle2,
  AlertTriangle,
  Copy,
  Check,
  ExternalLink,
  Plus,
  RefreshCw,
  Trash2,
  Sliders,
  Send,
  Zap,
  Lock,
  Download,
  FileCode,
  Cpu,
  ArrowRight,
  BookOpen,
  Eye,
  EyeOff,
  Bell,
  HardDrive
} from 'lucide-react';
import { AppView } from '../../types';
import {
  DEVELOPER_EXECUTIVE_METRICS,
  API_PRODUCTS,
  API_ENDPOINTS,
  MOCK_DEVELOPER_KEYS,
  MOCK_OAUTH_CLIENTS,
  MOCK_WEBHOOK_ENDPOINTS,
  MOCK_WEBHOOK_LOGS,
  MOCK_EMBEDDABLE_WIDGETS,
  MOCK_PUBLIC_DATA_RIGHTS,
  generateCodeSnippet
} from '../../data/developerMockData';
import {
  APIProduct,
  APIEndpointDefinition,
  DeveloperKey,
  OAuthClient,
  WebhookEndpoint,
  WebhookDeliveryLog,
  EmbeddableWidgetConfig
} from '../../services/developer/apiProductTypes';

type DevPortalTab =
  | 'overview'
  | 'api-reference'
  | 'api-keys'
  | 'oauth'
  | 'webhooks'
  | 'widgets'
  | 'usage-metering'
  | 'sdks'
  | 'data-rights';

interface Props {
  onNavigateToView: (v: AppView) => void;
}

export const DeveloperPortal: React.FC<Props> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<DevPortalTab>('overview');
  const [selectedLanguage, setSelectedLanguage] = useState<'CURL' | 'TYPESCRIPT' | 'PYTHON'>('CURL');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  
  // Interactive API Keys state
  const [keys, setKeys] = useState<DeveloperKey[]>(MOCK_DEVELOPER_KEYS);
  const [newKeyModalOpen, setNewKeyModalOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyEnv, setNewKeyEnv] = useState<'SANDBOX' | 'PRODUCTION'>('SANDBOX');
  const [createdKeyPlaintext, setCreatedKeyPlaintext] = useState<string | null>(null);

  // Interactive Endpoint Explorer State
  const [selectedEndpoint, setSelectedEndpoint] = useState<APIEndpointDefinition>(API_ENDPOINTS[0]);
  const [customRequestJson, setCustomRequestJson] = useState(
    JSON.stringify(API_ENDPOINTS[0].sampleRequest, null, 2)
  );
  const [apiResponseOutput, setApiResponseOutput] = useState<any>(API_ENDPOINTS[0].sampleResponse);
  const [isExecutingApi, setIsExecutingApi] = useState(false);

  // Webhooks State
  const [webhookLogs, setWebhookLogs] = useState<WebhookDeliveryLog[]>(MOCK_WEBHOOK_LOGS);
  const [isDispatchingTestWebhook, setIsDispatchingTestWebhook] = useState(false);
  const [testWebhookNotice, setTestWebhookNotice] = useState<string | null>(null);

  // Widgets State
  const [widgets, setWidgets] = useState<EmbeddableWidgetConfig[]>(MOCK_EMBEDDABLE_WIDGETS);
  const [selectedWidget, setSelectedWidget] = useState<EmbeddableWidgetConfig>(MOCK_EMBEDDABLE_WIDGETS[0]);
  const [widgetPreviewCurrency, setWidgetPreviewCurrency] = useState('USD');
  const [widgetConvertedResult, setWidgetConvertedResult] = useState('924.50 EUR');

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(id);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleCreateKey = () => {
    if (!newKeyName.trim()) return;
    const randomHex = Math.random().toString(36).substring(2, 12);
    const prefix = newKeyEnv === 'SANDBOX' ? `yp_test_${randomHex}` : `yp_live_${randomHex}`;
    const fullSecret = `${prefix}_${Math.random().toString(36).substring(2, 18)}`;

    const newKey: DeveloperKey = {
      id: `key-${Date.now()}`,
      name: newKeyName,
      keyPrefix: `${prefix}...`,
      environment: newKeyEnv,
      organizationId: 'org-acme-fintech',
      projectId: 'proj-acme-core',
      scopes: ['currency.read', 'fx.convert', 'salary.calculate', 'loan.calculate', 'wealth.calculate'],
      createdAt: new Date().toISOString(),
      lastUsedAt: null,
      expiresAt: null,
      status: 'ACTIVE'
    };

    setKeys([newKey, ...keys]);
    setCreatedKeyPlaintext(fullSecret);
    setNewKeyName('');
  };

  const handleExecuteSandboxApi = () => {
    setIsExecutingApi(true);
    setTimeout(() => {
      try {
        const parsed = JSON.parse(customRequestJson);
        // Realistic simulated response adhering to the contract
        let simulated: any = {
          requestId: `req_${Math.random().toString(36).substring(2, 10)}`,
          status: 'SUCCESS',
          environment: 'SANDBOX',
          computeUnitsConsumed: selectedEndpoint.computeUnits,
          timestamp: new Date().toISOString(),
          ...selectedEndpoint.sampleResponse
        };
        if (parsed.amount) {
          simulated.inputAmount = String(parsed.amount);
          simulated.convertedAmount = (Number(parsed.amount) * 0.9245).toFixed(2);
        }
        setApiResponseOutput(simulated);
      } catch (err) {
        setApiResponseOutput({
          type: 'https://api.yamoopay.com/errors/invalid_json_payload',
          title: 'Bad Request Payload',
          status: 400,
          detail: 'Unable to parse JSON body string. Verify structure.'
        });
      }
      setIsExecutingApi(false);
    }, 280);
  };

  const handleDispatchTestWebhook = () => {
    setIsDispatchingTestWebhook(true);
    setTimeout(() => {
      const newLog: WebhookDeliveryLog = {
        id: `del-log-${Date.now()}`,
        endpointId: 'wh-ep-01',
        eventId: `evt_manual_test_${Math.random().toString(36).substring(2, 8)}`,
        eventType: 'fx.rate_alert.triggered',
        payload: {
          alertId: 'alt_test_live',
          pair: 'GBP/USD',
          triggerRate: '1.3150',
          observedRate: '1.3155',
          timestamp: new Date().toISOString()
        },
        httpStatus: 200,
        durationMs: 38,
        timestamp: new Date().toISOString(),
        status: 'DELIVERED',
        attemptNumber: 1,
        signatureHeader: `t=${Math.floor(Date.now() / 1000)},v1=${Math.random().toString(36).substring(2, 24)}`
      };
      setWebhookLogs([newLog, ...webhookLogs]);
      setIsDispatchingTestWebhook(false);
      setTestWebhookNotice('Test webhook delivered successfully with HMAC-SHA256 signature!');
      setTimeout(() => setTestWebhookNotice(null), 4000);
    }, 350);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] pb-24 text-[#0F172A]">
      {/* Header Banner */}
      <div className="bg-[#0F172A] text-white pt-16 pb-24 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[30rem] h-[30rem] bg-[#1E40AF] rounded-full blur-[140px] opacity-25 pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="flex items-center gap-3 text-[#94A3B8] mb-4 text-sm font-medium">
            <Code className="w-4 h-4 text-[#38BDF8]" />
            <span>YamooPay Developer Ecosystem</span>
            <span>/</span>
            <span className="text-white font-bold">Public Financial API Platform (Prompt 20)</span>
          </div>

          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl md:text-5xl font-black tracking-tight text-white mb-3">
                YamooPay Developer Platform
              </h1>
              <p className="text-[#CBD5E1] text-base md:text-lg max-w-3xl leading-relaxed">
                Build mission-critical financial applications with deterministic REST APIs, OpenAPI 3.x contracts, high-assurance webhooks, embeddable widgets, and SDKs.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => onNavigateToView('developer-command-center')}
                className="px-4 py-2.5 bg-blue-900/60 hover:bg-blue-900 border border-blue-400/40 text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Sliders className="w-4 h-4 text-[#38BDF8]" />
                <span>Command Center</span>
              </button>

              <div className="px-4 py-2 bg-emerald-950/60 border border-emerald-500/40 text-emerald-300 rounded-xl text-xs font-bold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span>API Status: 99.98% Operational</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Container */}
      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-20">
        <div className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-6 mb-8">
          {/* Sub Navigation Tabs */}
          <div className="flex flex-wrap gap-2 mb-8 border-b border-[#E2E8F0] pb-4">
            {[
              { id: 'overview', label: 'Quickstart & Overview', icon: Activity },
              { id: 'api-reference', label: 'API Reference & Explorer', icon: Terminal },
              { id: 'api-keys', label: `API Keys (${keys.length})`, icon: Key },
              { id: 'oauth', label: 'OAuth 2.0 & OIDC', icon: Lock },
              { id: 'webhooks', label: 'Webhooks & Subscriptions', icon: Bell },
              { id: 'widgets', label: 'Embeddable Widgets', icon: Globe },
              { id: 'usage-metering', label: 'Usage & Quotas', icon: Cpu },
              { id: 'sdks', label: 'SDKs & Libraries', icon: Download },
              { id: 'data-rights', label: 'Data Rights & Compliance', icon: Shield },
            ].map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as DevPortalTab)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#1E40AF] text-white shadow-xs'
                      : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>

          {/* TAB 1: OVERVIEW & QUICKSTART */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Policy Banner: Public API Boundary */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start gap-4">
                <Shield className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-blue-950 font-bold text-base mb-1">
                    CANONICAL PUBLIC API BOUNDARY & STRICT DETERMINISM
                  </h3>
                  <p className="text-blue-900 text-sm leading-relaxed">
                    Internal database models and unverified third-party endpoints are never exposed. All YamooPay Public APIs enforce strict decimal precision strings, explicit ISO currencies, timezones, and RFC 7807 problem details.
                  </p>
                </div>
              </div>

              {/* KPI Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Time to First Call</div>
                  <div className="text-3xl font-black text-[#1E40AF]">{DEVELOPER_EXECUTIVE_METRICS.timeToFirstSuccessMinutes} min</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">88.4% activation rate</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Monthly API Volume</div>
                  <div className="text-3xl font-black text-[#0F172A]">{DEVELOPER_EXECUTIVE_METRICS.monthlyAPIRequests}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">99.98% successful responses</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">P95 Global Latency</div>
                  <div className="text-3xl font-black text-[#0F172A]">{DEVELOPER_EXECUTIVE_METRICS.p95LatencyMs} ms</div>
                  <div className="text-xs text-[#64748B] font-bold mt-1">Multi-region edge gateway</div>
                </div>

                <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-5">
                  <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1">Widget Impressions</div>
                  <div className="text-3xl font-black text-[#0F172A]">{DEVELOPER_EXECUTIVE_METRICS.monthlyWidgetImpressions}</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">Secure sandbox iframe</div>
                </div>
              </div>

              {/* 3-Minute Interactive Quickstart */}
              <div className="border border-[#E2E8F0] rounded-2xl p-6 bg-white shadow-xs">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2">
                      <Zap className="w-5 h-5 text-amber-500" />
                      <span>3-Minute Quickstart (Sandbox)</span>
                    </h3>
                    <p className="text-sm text-[#64748B]">
                      Execute your first live FX conversion API call using our verified sandbox environment.
                    </p>
                  </div>

                  <div className="flex gap-2 bg-[#F1F5F9] p-1 rounded-xl">
                    {(['CURL', 'TYPESCRIPT', 'PYTHON'] as const).map((lang) => (
                      <button
                        key={lang}
                        onClick={() => setSelectedLanguage(lang)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                          selectedLanguage === lang
                            ? 'bg-[#1E40AF] text-white shadow-xs'
                            : 'text-[#64748B] hover:text-[#0F172A]'
                        }`}
                      >
                        {lang}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="relative">
                  <pre className="bg-[#0F172A] text-[#38BDF8] p-5 rounded-xl text-xs font-mono overflow-x-auto leading-relaxed border border-[#1E293B]">
                    {generateCodeSnippet(selectedLanguage, 'yp_test_41c098df23')}
                  </pre>
                  <button
                    onClick={() => copyToClipboard(generateCodeSnippet(selectedLanguage, 'yp_test_41c098df23'), 'quickstart-code')}
                    className="absolute top-3 right-3 px-3 py-1.5 bg-[#1E293B] hover:bg-[#334155] text-white text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors cursor-pointer"
                  >
                    {copiedKey === 'quickstart-code' ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                    <span>{copiedKey === 'quickstart-code' ? 'Copied' : 'Copy'}</span>
                  </button>
                </div>
              </div>

              {/* API Product Families Cards */}
              <div>
                <h3 className="text-lg font-bold text-[#0F172A] mb-4">Core Financial API Products</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {API_PRODUCTS.map((prod) => (
                    <div
                      key={prod.id}
                      onClick={() => {
                        setActiveTab('api-reference');
                        const matched = API_ENDPOINTS.find((e) => e.productId === prod.id);
                        if (matched) setSelectedEndpoint(matched);
                      }}
                      className="border border-[#E2E8F0] hover:border-[#1E40AF] rounded-2xl p-5 bg-white transition-all hover:shadow-xs cursor-pointer group"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-bold text-[#0F172A] group-hover:text-[#1E40AF] text-base">{prod.name}</span>
                        <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded border border-emerald-200">
                          {prod.stability}
                        </span>
                      </div>
                      <p className="text-xs text-[#64748B] mb-4 leading-relaxed line-clamp-2">{prod.description}</p>
                      <div className="flex items-center justify-between text-xs text-[#64748B] pt-3 border-t border-[#F1F5F9]">
                        <span>{prod.endpointsCount} Endpoints</span>
                        <span className="font-mono text-[#059669] font-bold">{prod.p95LatencyMs} ms P95</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: API REFERENCE & EXPLORER */}
          {activeTab === 'api-reference' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Sidebar Endpoint List */}
              <div className="lg:col-span-4 border border-[#E2E8F0] rounded-2xl p-4 bg-[#F8FAFC] space-y-2">
                <div className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-2">API Endpoints (OpenAPI 3.1)</div>
                {API_ENDPOINTS.map((ep) => (
                  <button
                    key={ep.id}
                    onClick={() => {
                      setSelectedEndpoint(ep);
                      setCustomRequestJson(JSON.stringify(ep.sampleRequest, null, 2));
                      setApiResponseOutput(ep.sampleResponse);
                    }}
                    className={`w-full text-left p-3 rounded-xl text-xs font-bold transition-all flex items-center justify-between cursor-pointer ${
                      selectedEndpoint.id === ep.id
                        ? 'bg-[#1E40AF] text-white shadow-xs'
                        : 'bg-white text-[#475569] hover:bg-[#F1F5F9] border border-[#E2E8F0]'
                    }`}
                  >
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
                          selectedEndpoint.id === ep.id ? 'bg-blue-800 text-white' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {ep.method}
                        </span>
                        <span className="font-mono">{ep.path}</span>
                      </div>
                      <div className="text-[11px] opacity-80 mt-1">{ep.summary}</div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Main Interactive Playground */}
              <div className="lg:col-span-8 border border-[#E2E8F0] rounded-2xl p-6 bg-white space-y-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 pb-4 border-b border-[#E2E8F0]">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-mono text-xs font-bold rounded">
                        {selectedEndpoint.method}
                      </span>
                      <h3 className="font-mono text-sm font-bold text-[#0F172A]">{selectedEndpoint.path}</h3>
                    </div>
                    <p className="text-xs text-[#64748B] mt-1">{selectedEndpoint.description}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 bg-[#F1F5F9] text-[#475569] text-xs font-bold rounded">
                      {selectedEndpoint.computeUnits} Compute Units
                    </span>
                    <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded border border-emerald-200">
                      Rate Limit: {selectedEndpoint.rateLimitPerMinute}/min
                    </span>
                  </div>
                </div>

                {/* Scopes Required */}
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#64748B] font-bold">Required Scopes:</span>
                  {selectedEndpoint.requiredScopes.map((scope) => (
                    <span key={scope} className="px-2 py-0.5 bg-blue-50 border border-blue-200 text-blue-800 rounded font-mono text-[11px]">
                      {scope}
                    </span>
                  ))}
                </div>

                {/* Request Payload Editor */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-xs text-[#475569] uppercase tracking-wider">Request Body (JSON)</span>
                    <button
                      onClick={handleExecuteSandboxApi}
                      disabled={isExecutingApi}
                      className="px-4 py-1.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                    >
                      {isExecutingApi ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                      <span>{isExecutingApi ? 'Executing...' : 'Run Request (Sandbox)'}</span>
                    </button>
                  </div>
                  <textarea
                    value={customRequestJson}
                    onChange={(e) => setCustomRequestJson(e.target.value)}
                    rows={6}
                    className="w-full bg-[#0F172A] text-[#38BDF8] p-4 rounded-xl font-mono text-xs border border-[#1E293B] focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                  />
                </div>

                {/* Live Response Viewer */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-bold text-xs text-[#475569] uppercase tracking-wider">Live Response (application/json)</span>
                    <span className="text-[11px] font-mono text-emerald-600 font-bold">HTTP 200 OK — 28ms</span>
                  </div>
                  <pre className="bg-[#0F172A] text-emerald-400 p-4 rounded-xl font-mono text-xs overflow-x-auto border border-[#1E293B] max-h-72">
                    {JSON.stringify(apiResponseOutput, null, 2)}
                  </pre>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: API KEYS & SCOPES */}
          {activeTab === 'api-keys' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A]">Developer API Keys</h3>
                  <p className="text-sm text-[#64748B]">
                    Server-to-server credentials with cryptographic hashing and granular least-privilege scopes.
                  </p>
                </div>

                <button
                  onClick={() => setNewKeyModalOpen(true)}
                  className="px-4 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
                >
                  <Plus className="w-4 h-4" />
                  <span>Create API Key</span>
                </button>
              </div>

              {/* New Key Revealed Banner */}
              {createdKeyPlaintext && (
                <div className="p-5 bg-amber-50 border border-amber-300 rounded-xl space-y-2">
                  <div className="flex items-center gap-2 text-amber-950 font-bold text-sm">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                    <span>Copy your API Key Secret Now</span>
                  </div>
                  <p className="text-xs text-amber-900 leading-relaxed">
                    This secret is displayed <strong>only once</strong> and is immediately hashed. It cannot be recovered later.
                  </p>
                  <div className="flex items-center gap-2 pt-1">
                    <code className="bg-white border border-amber-300 px-3 py-2 rounded-lg font-mono text-xs font-bold text-amber-950 flex-1 break-all">
                      {createdKeyPlaintext}
                    </code>
                    <button
                      onClick={() => copyToClipboard(createdKeyPlaintext, 'new-secret')}
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold transition-colors cursor-pointer"
                    >
                      {copiedKey === 'new-secret' ? 'Copied' : 'Copy Secret'}
                    </button>
                  </div>
                </div>
              )}

              {/* API Keys Table */}
              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Key Name</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Prefix</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Environment</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Scopes</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Last Used</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {keys.map((k) => (
                      <tr key={k.id} className="hover:bg-[#F8FAFC]">
                        <td className="px-4 py-3 font-bold text-[#0F172A]">{k.name}</td>
                        <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{k.keyPrefix}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            k.environment === 'PRODUCTION'
                              ? 'bg-purple-100 text-purple-800'
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {k.environment}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-xs text-[#64748B]">{k.scopes.length} Scopes Assigned</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">
                          {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded">
                            {k.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Create Key Modal Dialog */}
              {newKeyModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl border border-[#E2E8F0] space-y-4">
                    <h3 className="text-lg font-bold text-[#0F172A]">Generate New API Key</h3>
                    <div>
                      <label className="block text-xs font-bold text-[#475569] mb-1">Key Description / Name</label>
                      <input
                        type="text"
                        placeholder="e.g. Backend Microservice Worker"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        className="w-full px-3 py-2 border border-[#CBD5E1] rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1E40AF]"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-[#475569] mb-1">Target Environment</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => setNewKeyEnv('SANDBOX')}
                          className={`p-2.5 rounded-xl text-xs font-bold border cursor-pointer ${
                            newKeyEnv === 'SANDBOX'
                              ? 'bg-blue-50 border-[#1E40AF] text-[#1E40AF]'
                              : 'bg-white border-[#E2E8F0] text-[#64748B]'
                          }`}
                        >
                          Sandbox (Test Data)
                        </button>
                        <button
                          onClick={() => setNewKeyEnv('PRODUCTION')}
                          className={`p-2.5 rounded-xl text-xs font-bold border cursor-pointer ${
                            newKeyEnv === 'PRODUCTION'
                              ? 'bg-purple-50 border-purple-600 text-purple-700'
                              : 'bg-white border-[#E2E8F0] text-[#64748B]'
                          }`}
                        >
                          Production (Live API)
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-[#E2E8F0]">
                      <button
                        onClick={() => setNewKeyModalOpen(false)}
                        className="px-4 py-2 text-[#64748B] hover:bg-[#F1F5F9] rounded-xl text-xs font-bold transition-colors cursor-pointer"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          handleCreateKey();
                          setNewKeyModalOpen(false);
                        }}
                        className="px-4 py-2 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-xs font-bold transition-colors cursor-pointer"
                      >
                        Generate Key
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* TAB 4: OAUTH 2.0 & OIDC */}
          {activeTab === 'oauth' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">OAuth 2.0 Authorization Server & OIDC</h3>
                <p className="text-sm text-[#64748B]">
                  Delegated third-party access using Authorization Code + PKCE (RFC 7636). Never used for raw API keys.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {MOCK_OAUTH_CLIENTS.map((client) => (
                  <div key={client.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-bold text-base text-[#0F172A]">{client.name}</h4>
                        <div className="text-xs text-[#64748B] font-mono mt-0.5">Client ID: {client.clientId}</div>
                      </div>
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-bold rounded">
                        {client.clientType}
                      </span>
                    </div>

                    <div>
                      <span className="text-xs font-bold text-[#475569]">Authorized Redirect URIs:</span>
                      <ul className="mt-1 space-y-1">
                        {client.redirectUris.map((uri) => (
                          <li key={uri} className="text-xs font-mono text-[#64748B] bg-[#F8FAFC] p-1.5 rounded border border-[#E2E8F0]">
                            {uri}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-[#F1F5F9] text-xs">
                      <span className="text-[#059669] font-bold">{client.totalGrants} Active User Grants</span>
                      <span className="text-[#64748B]">PKCE: ENFORCED</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 5: WEBHOOKS */}
          {activeTab === 'webhooks' && (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A]">Event Webhooks & Cryptographic Signatures</h3>
                  <p className="text-sm text-[#64748B]">
                    Asynchronous delivery with HMAC-SHA256 timestamped signatures (<code className="font-mono text-xs">yp-signature</code>) and finite exponential retries.
                  </p>
                </div>

                <button
                  onClick={handleDispatchTestWebhook}
                  disabled={isDispatchingTestWebhook}
                  className="px-4 py-2.5 bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-xl text-xs font-bold flex items-center gap-2 transition-colors cursor-pointer"
                >
                  <Send className="w-4 h-4" />
                  <span>{isDispatchingTestWebhook ? 'Dispatching...' : 'Send Test Webhook'}</span>
                </button>
              </div>

              {testWebhookNotice && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl text-sm font-bold">
                  {testWebhookNotice}
                </div>
              )}

              {/* Webhook Endpoints List */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {MOCK_WEBHOOK_ENDPOINTS.map((wh) => (
                  <div key={wh.id} className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <div className="flex justify-between items-start">
                      <div className="font-mono text-xs font-bold text-[#0F172A] break-all">{wh.url}</div>
                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-[10px] font-bold rounded">
                        {wh.status}
                      </span>
                    </div>

                    <div className="text-xs text-[#64748B]">
                      <span className="font-bold text-[#475569]">Subscribed Events:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {wh.subscribedEvents.map((evt) => (
                          <span key={evt} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded font-mono text-[10px]">
                            {evt}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-[#F1F5F9] text-xs text-[#64748B]">
                      <span>Secret: <strong className="font-mono">{wh.secretPreview}</strong></span>
                      <span className="text-[#059669] font-bold">{wh.successRatePct}% Success Rate</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Delivery Logs Table */}
              <div>
                <h4 className="font-bold text-sm text-[#0F172A] mb-3">Recent Webhook Deliveries</h4>
                <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                  <table className="w-full text-left text-sm">
                    <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                      <tr>
                        <th className="px-4 py-3 font-bold text-[#475569]">Event Type</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Event ID</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">HTTP Status</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Duration</th>
                        <th className="px-4 py-3 font-bold text-[#475569]">Timestamp</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {webhookLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="px-4 py-3 font-bold text-[#0F172A] font-mono text-xs">{log.eventType}</td>
                          <td className="px-4 py-3 font-mono text-xs text-[#64748B]">{log.eventId}</td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-mono text-xs font-bold rounded">
                              {log.httpStatus} OK
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs font-mono">{log.durationMs} ms</td>
                          <td className="px-4 py-3 text-xs text-[#64748B]">{new Date(log.timestamp).toLocaleTimeString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* TAB 6: EMBEDDABLE WIDGETS */}
          {activeTab === 'widgets' && (
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h3 className="text-lg font-bold text-[#0F172A]">Embeddable Financial Widgets</h3>
                  <p className="text-sm text-[#64748B]">
                    Secure, responsive, light-mode iframe calculators for partner blogs and real estate portals.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Configurator */}
                <div className="lg:col-span-6 space-y-4">
                  <div className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                    <label className="block text-xs font-bold text-[#475569] uppercase tracking-wider">Select Widget Template</label>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {widgets.map((w) => (
                        <button
                          key={w.id}
                          onClick={() => setSelectedWidget(w)}
                          className={`p-3 rounded-xl text-left border text-xs font-bold transition-all cursor-pointer ${
                            selectedWidget.id === w.id
                              ? 'bg-blue-50 border-[#1E40AF] text-[#1E40AF]'
                              : 'bg-[#F8FAFC] border-[#E2E8F0] text-[#64748B]'
                          }`}
                        >
                          {w.name.split(' ')[0]} Calc
                        </button>
                      ))}
                    </div>

                    <div className="pt-2">
                      <label className="block text-xs font-bold text-[#475569] mb-1">Allowed Host Domains</label>
                      <div className="text-xs text-[#64748B] bg-[#F8FAFC] p-2 rounded-lg font-mono">
                        {selectedWidget.allowedDomains.join(', ')}
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between items-center mb-1">
                        <label className="text-xs font-bold text-[#475569]">HTML Embed Snippet</label>
                        <button
                          onClick={() => copyToClipboard(selectedWidget.iframeSnippet, 'widget-code')}
                          className="text-[#1E40AF] text-xs font-bold hover:underline cursor-pointer"
                        >
                          {copiedKey === 'widget-code' ? 'Copied!' : 'Copy Code'}
                        </button>
                      </div>
                      <pre className="bg-[#0F172A] text-[#38BDF8] p-3 rounded-xl text-xs font-mono overflow-x-auto">
                        {selectedWidget.iframeSnippet}
                      </pre>
                    </div>
                  </div>
                </div>

                {/* Right Interactive Live Widget Preview */}
                <div className="lg:col-span-6 border border-[#CBD5E1] rounded-2xl p-6 bg-white shadow-xs">
                  <div className="flex justify-between items-center mb-4 pb-3 border-b border-[#E2E8F0]">
                    <span className="text-xs font-bold text-[#475569] uppercase tracking-wider">Live Embed Preview (Light Mode)</span>
                    <span className="text-xs font-bold text-emerald-600">Sandboxed Iframe Mode</span>
                  </div>

                  <div className="border border-[#E2E8F0] rounded-xl p-5 bg-[#F8FAFC] space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-black text-sm text-[#0F172A]">{selectedWidget.name}</span>
                      <span className="text-[10px] font-bold text-[#64748B]">YamooPay Widget v1.4</span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-[#64748B]">Amount</label>
                        <input
                          type="number"
                          defaultValue={1000}
                          className="w-full bg-white border border-[#CBD5E1] px-3 py-1.5 rounded-lg text-sm font-bold text-[#0F172A]"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-bold text-[#64748B]">Target Currency</label>
                        <select
                          value={widgetPreviewCurrency}
                          onChange={(e) => {
                            setWidgetPreviewCurrency(e.target.value);
                            setWidgetConvertedResult(e.target.value === 'EUR' ? '924.50 EUR' : '790.20 GBP');
                          }}
                          className="w-full bg-white border border-[#CBD5E1] px-3 py-1.5 rounded-lg text-sm font-bold text-[#0F172A]"
                        >
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="CAD">CAD ($)</option>
                        </select>
                      </div>
                    </div>

                    <div className="p-3 bg-white border border-[#E2E8F0] rounded-xl flex justify-between items-center">
                      <span className="text-xs text-[#64748B]">Calculated Value:</span>
                      <span className="text-base font-black text-[#1E40AF]">{widgetConvertedResult}</span>
                    </div>

                    <div className="text-center pt-2">
                      <span className="text-[10px] text-[#94A3B8] font-bold">
                        Powered by <strong>YamooPay Financial Intelligence</strong>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 7: USAGE & QUOTAS */}
          {activeTab === 'usage-metering' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">API Usage & Compute Metering</h3>
                <p className="text-sm text-[#64748B]">
                  Durable billing ledger with idempotent compute unit tracking across organization projects.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="border border-[#E2E8F0] rounded-xl p-5 bg-[#F8FAFC]">
                  <span className="text-xs font-bold text-[#64748B] uppercase">Monthly Included Quota</span>
                  <div className="text-2xl font-black text-[#0F172A] mt-1">25,000,000 Units</div>
                  <div className="text-xs text-[#64748B] mt-1">Enterprise Tier</div>
                </div>

                <div className="border border-[#E2E8F0] rounded-xl p-5 bg-[#F8FAFC]">
                  <span className="text-xs font-bold text-[#64748B] uppercase">Current Period Consumption</span>
                  <div className="text-2xl font-black text-[#1E40AF] mt-1">14,200,000 Units</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">56.8% of monthly limit</div>
                </div>

                <div className="border border-[#E2E8F0] rounded-xl p-5 bg-[#F8FAFC]">
                  <span className="text-xs font-bold text-[#64748B] uppercase">Estimated Next Period Overage</span>
                  <div className="text-2xl font-black text-[#0F172A] mt-1">$0.00</div>
                  <div className="text-xs text-[#059669] font-bold mt-1">Within included allowance</div>
                </div>
              </div>

              {/* Quota Progress Bar */}
              <div className="border border-[#E2E8F0] rounded-xl p-5 bg-white">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-bold text-sm text-[#0F172A]">Quota Consumption (August 2026)</span>
                  <span className="text-sm font-bold text-[#64748B]">14.2M / 25.0M Compute Units</span>
                </div>
                <div className="w-full bg-[#F1F5F9] rounded-full h-3 overflow-hidden">
                  <div className="bg-[#1E40AF] h-full rounded-full transition-all" style={{ width: '56.8%' }} />
                </div>
              </div>
            </div>
          )}

          {/* TAB 8: SDKS & LIBRARIES */}
          {activeTab === 'sdks' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Official Client SDKs</h3>
                <p className="text-sm text-[#64748B]">
                  Strongly-typed client libraries with automatic retries, exponential backoff, and full TypeScript typing.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-base text-[#0F172A]">Node.js & TypeScript SDK</h4>
                      <code className="text-xs text-[#1E40AF] font-bold font-mono">npm install @yamoopay/sdk</code>
                    </div>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                      v1.4.2 (GA)
                    </span>
                  </div>
                  <p className="text-xs text-[#64748B] leading-relaxed">
                    Zero-dependency client with native fetch, typed error classes (RateLimitExceededError, InvalidCurrencyError), and streaming support.
                  </p>
                </div>

                <div className="border border-[#E2E8F0] rounded-2xl p-5 bg-white space-y-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-base text-[#0F172A]">Python SDK</h4>
                      <code className="text-xs text-[#1E40AF] font-bold font-mono">pip install yamoopay-python</code>
                    </div>
                    <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs font-bold rounded">
                      v1.3.0 (GA)
                    </span>
                  </div>
                  <p className="text-xs text-[#64748B] leading-relaxed">
                    Supports sync and async (asyncio) operations, Pydantic decimal parsing, and automatic HTTP retry backoff.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 9: DATA RIGHTS */}
          {activeTab === 'data-rights' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-bold text-[#0F172A]">Public Data Redistribution & Licensing Matrix</h3>
                <p className="text-sm text-[#64748B]">
                  Strict enforcement of legal and commercial redistribution rights prior to any API endpoint launch.
                </p>
              </div>

              <div className="border border-[#E2E8F0] rounded-xl overflow-hidden bg-white">
                <table className="w-full text-left text-sm">
                  <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                    <tr>
                      <th className="px-4 py-3 font-bold text-[#475569]">Dataset Name</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Primary Source</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Redistribution Class</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Commercial Use</th>
                      <th className="px-4 py-3 font-bold text-[#475569]">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#E2E8F0]">
                    {MOCK_PUBLIC_DATA_RIGHTS.map((dr) => (
                      <tr key={dr.id}>
                        <td className="px-4 py-3 font-bold text-[#0F172A]">{dr.datasetName}</td>
                        <td className="px-4 py-3 text-xs text-[#64748B]">{dr.primarySource}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            dr.redistributionClass === 'PUBLIC_REDISTRIBUTABLE'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {dr.redistributionClass}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-bold">
                          {dr.commercialUseAllowed ? (
                            <span className="text-[#059669]">PERMITTED</span>
                          ) : (
                            <span className="text-red-600">RESTRICTED</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs font-bold rounded">
                            {dr.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
